import { Back } from "@gisce/oficina-virtual-components";
import { CouponsView } from "./CouponsView/CouponsView";
import { Box, Paper, PaperProps, useMediaQuery, useTheme } from '@mui/material';
import FeatureFlag from "./FeatureFlag/FeatureFlag";
import { useSettingsContext } from "./Settings/hooks/useSettingsContext";
import { useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";


interface ILayoutProvider {
  isError?: boolean;
  path?: string;
  children: React.ReactNode;
}

// This provider checks the url and the current cookies for authentication
export const LayoutProvider = ({
  isError,
  path = "",
  children
}: ILayoutProvider) => {
  const Settings = useSettingsContext();
  const theme = useTheme();
  const { t, ready } = useTranslation();
  const params = useParams();
  const { pathname } = useLocation();
  const breakpointDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const breakpointUpLg = useMediaQuery(theme.breakpoints.up('lg'));

  const isInContractPath = () => {
    return Object.values(t('common:url.contracts', { returnObjects: true, ...params }))
      .map(type => Object.values(type).includes(pathname))
      .some(val => val === true)
  }

  const paperWrapperProps: PaperProps = {
    elevation: 5,
  }

  const locationPaperOptions = {
    sm: {
      breakpoint: 'sm',
      maxWidth: 500,
      upBreakpointSx: {
        marginTop: 5,
        marginBottom: 3,
      }
    },
    publicityBanner: {
      downBreakpointSx: {
        padding: 0
      },
      upBreakpointSx: {
        marginTop: 3,
      }
    },
    lg: {
      upBreakpointSx: {
        marginTop: 4,
        marginBottom: 3,
      }
    }
  }

  // Es fa així per evitar ensenyar-lo en alguns llocs
  const hidePaper = [
    (!Settings.home?.showContainerPaper && ''),
    'common:url.contracts.electricity.default',
    'common:url.contracts.electricity.detail',
    'common:url.contracts.gas.default',
    'common:url.contracts.gas.detail'
  ];

  const displayPaper = !(hidePaper.indexOf(path) > -1) && !isError;

  const hideStackPadding = [
    ...(breakpointDownMd ? ['common:url.dualContractation'] : []),
    ...(breakpointDownMd ? ['common:url.electricityContractation'] : []),
    ...(breakpointDownMd ? ['common:url.gasContractation'] : []),
    ...(breakpointUpLg ? ['common:url.contracts.electricity.default'] : []),
    ...(breakpointUpLg ? ['common:url.contracts.electricity.detail'] : []),
    ...(breakpointUpLg ? ['common:url.contracts.gas.default'] : []),
    ...(breakpointUpLg ? ['common:url.contracts.gas.detail'] : []),
  ];

  const displayStackPadding = !(hideStackPadding.indexOf(path) > -1) && !isError;

  const smContainers = [
    'common:url.activate',
    'common:url.login',
    'common:url.email_sent',
    'common:url.changePassword',
    'common:url.resetPassword',
    'common:url.contact',
    'common:url.unsigned_contact'
  ];

  const getPaperOptions = () => {
    const smPapers = [
      'common:url.activate',
      'common:url.login',
      'common:url.email_sent',
      'common:url.changePassword',
      'common:url.resetPassword',
    ];

    if (smPapers.indexOf(path) > -1) {
      return (locationPaperOptions.sm);
    } else {
      return (locationPaperOptions.lg);
    }
  }

  const isSmallContainer = smContainers.indexOf(path) > -1;

  const showCouponsPaths = [
    'common:url.invoices'
  ];

  const displayCoupons = (
    ready && (showCouponsPaths.indexOf(path) > -1 || isInContractPath())
  )

  return <>
    {displayCoupons && (
      <FeatureFlag flagName="coupons.coupon">
        <Back 
          {...locationPaperOptions["publicityBanner"]} 
          wrapperProps={{ elevation: 0, sx: { borderRadius: 2 } }} 
          wrapperComponent={Paper}
        >
          <CouponsView />
        </Back>
      </FeatureFlag>
    )}

    <Back 
      {...getPaperOptions()} 
      wrapperProps={displayPaper ? paperWrapperProps : {}} 
      wrapperComponent={displayPaper ? Paper : Box}
    >
      {isSmallContainer ?
        <Box p={4}>
          {children}
        </Box>
        :
        <Box
          sx={{
            padding: displayStackPadding ? 4 : 0
          }}
        >
          {children}
        </Box>
      }
    </Back>
  </>

}
